/**
 * WebAudio recipe functions — pure, zero-asset sound design. Every recipe
 * takes an already-running AudioContext, a destination node, and an absolute
 * start time, so the engine can schedule anything a frame early.
 */

let sharedNoise: AudioBuffer | null = null;

/** Cached white-noise buffer (2 s) shared by every recipe. */
export function noiseBuffer(ctx: AudioContext): AudioBuffer {
  if (sharedNoise && sharedNoise.sampleRate === ctx.sampleRate) return sharedNoise;
  const len = Math.floor(ctx.sampleRate * 2);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  sharedNoise = buf;
  return buf;
}

function env(ctx: AudioContext, dest: AudioNode, t0: number, attack: number, dur: number, peak: number): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  g.connect(dest);
  return g;
}

/** Filtered noise whoosh — the body of explosions, splashes, footsteps. */
export function noiseBurst(
  ctx: AudioContext,
  dest: AudioNode,
  t0: number,
  opts: {
    dur: number;
    gain: number;
    /** Filter sweep start → end, Hz. */
    f0: number;
    f1: number;
    type?: BiquadFilterType;
    q?: number;
  },
): void {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  src.loop = true;
  const filt = ctx.createBiquadFilter();
  filt.type = opts.type ?? 'lowpass';
  filt.Q.value = opts.q ?? 1;
  filt.frequency.setValueAtTime(opts.f0, t0);
  filt.frequency.exponentialRampToValueAtTime(Math.max(40, opts.f1), t0 + opts.dur);
  const g = env(ctx, dest, t0, 0.005, opts.dur, opts.gain);
  src.connect(filt).connect(g);
  src.start(t0);
  src.stop(t0 + opts.dur + 0.05);
}

/** Pitched low thump (explosion weight, landings). Sine glides f0 → f1. */
export function thump(
  ctx: AudioContext,
  dest: AudioNode,
  t0: number,
  opts: { f0: number; f1: number; dur: number; gain: number },
): void {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(opts.f0, t0);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, opts.f1), t0 + opts.dur);
  const g = env(ctx, dest, t0, 0.004, opts.dur, opts.gain);
  osc.connect(g);
  osc.start(t0);
  osc.stop(t0 + opts.dur + 0.05);
}

/** Short pitched ping — bounces, UI taps, pickups. */
export function blip(
  ctx: AudioContext,
  dest: AudioNode,
  t0: number,
  opts: { freq: number; dur?: number; gain?: number; type?: OscillatorType; slide?: number },
): void {
  const dur = opts.dur ?? 0.09;
  const osc = ctx.createOscillator();
  osc.type = opts.type ?? 'triangle';
  osc.frequency.setValueAtTime(opts.freq, t0);
  if (opts.slide !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(30, opts.slide), t0 + dur);
  }
  const g = env(ctx, dest, t0, 0.003, dur, opts.gain ?? 0.25);
  osc.connect(g);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

/** Bright FM-ish chirp used for coins/pops (cluster splits, pickups). */
export function chirp(
  ctx: AudioContext,
  dest: AudioNode,
  t0: number,
  opts: { f0: number; f1: number; dur?: number; gain?: number },
): void {
  const dur = opts.dur ?? 0.14;
  const osc = ctx.createOscillator();
  osc.type = 'square';
  osc.frequency.setValueAtTime(opts.f0, t0);
  osc.frequency.exponentialRampToValueAtTime(Math.max(60, opts.f1), t0 + dur);
  const g = env(ctx, dest, t0, 0.004, dur, opts.gain ?? 0.16);
  const filt = ctx.createBiquadFilter();
  filt.type = 'lowpass';
  filt.frequency.value = 3200;
  osc.connect(filt).connect(g);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

/** Voice moods — the engine's `voice` event kinds. */
export type VoiceMood = 'chirp' | 'hurt' | 'die' | 'happy';

const MOOD_PITCH: Record<VoiceMood, number> = {
  chirp: 1,
  hurt: 0.82,
  die: 0.7,
  happy: 1.18,
};

/**
 * Character "voice blab": 2 oscillators (saw + pulse-ish square) through a
 * pair of bandpass formants, rapid syllable pitch steps → cute gibberish.
 * `pitch` scales the base (species table), `formant` the vocal size,
 * `rasp` mixes in a noise sparkle.
 */
export function voiceBlab(
  ctx: AudioContext,
  dest: AudioNode,
  t0: number,
  opts: {
    pitch: number;
    formant: number;
    rasp: number;
    mood: VoiceMood;
    syllables?: number;
  },
): void {
  const moodPitch = MOOD_PITCH;
  const base = 210 * opts.pitch * moodPitch[opts.mood];
  const syl = opts.syllables ?? (opts.mood === 'die' ? 3 : 2);
  const sylDur = opts.mood === 'die' ? 0.16 : 0.085;

  // Two formant bandpasses shape the raw buzz into "speech".
  const f1 = ctx.createBiquadFilter();
  f1.type = 'bandpass';
  f1.frequency.value = 620 * opts.formant;
  f1.Q.value = 4;
  const f2 = ctx.createBiquadFilter();
  f2.type = 'bandpass';
  f2.frequency.value = 1450 * opts.formant;
  f2.Q.value = 6;
  const mix = env(ctx, dest, t0, 0.01, syl * sylDur + 0.06, 0.5);
  f1.connect(mix);
  f2.connect(mix);

  const oscA = ctx.createOscillator();
  oscA.type = 'sawtooth';
  const oscB = ctx.createOscillator();
  oscB.type = 'square';
  const gB = ctx.createGain();
  gB.gain.value = 0.4;
  oscA.connect(f1);
  oscA.connect(f2);
  oscB.connect(gB);
  gB.connect(f1);
  gB.connect(f2);

  // Syllables: little pitch hops up/down per syllable.
  let t = t0;
  oscA.frequency.setValueAtTime(base, t);
  oscB.frequency.setValueAtTime(base * 1.005, t); // slight detune = thickness
  for (let i = 0; i < syl; i++) {
    const hop = 1 + (i % 2 === 0 ? 0.22 : -0.14) * (0.6 + Math.random() * 0.6);
    oscA.frequency.exponentialRampToValueAtTime(base * hop, t + sylDur * 0.6);
    oscB.frequency.exponentialRampToValueAtTime(base * hop * 1.005, t + sylDur * 0.6);
    t += sylDur;
  }

  // Rasp sparkle: tiny bandpassed noise riding along.
  if (opts.rasp > 0.05) {
    const n = ctx.createBufferSource();
    n.buffer = noiseBuffer(ctx);
    n.loop = true;
    const nf = ctx.createBiquadFilter();
    nf.type = 'bandpass';
    nf.frequency.value = 2400 * opts.formant;
    nf.Q.value = 2;
    const ng = ctx.createGain();
    ng.gain.value = opts.rasp * 0.18;
    n.connect(nf).connect(ng).connect(mix);
    n.start(t0);
    n.stop(t0 + syl * sylDur + 0.1);
  }

  oscA.start(t0);
  oscB.start(t0);
  oscA.stop(t0 + syl * sylDur + 0.1);
  oscB.stop(t0 + syl * sylDur + 0.1);
}

/** One plucked pentatonic note — the music loop's instrument. */
export function pluck(
  ctx: AudioContext,
  dest: AudioNode,
  t0: number,
  freq: number,
  dur: number,
  gain: number,
): void {
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = freq;
  const sub = ctx.createOscillator();
  sub.type = 'sine';
  sub.frequency.value = freq / 2;
  const subG = ctx.createGain();
  subG.gain.value = 0.3;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  const filt = ctx.createBiquadFilter();
  filt.type = 'lowpass';
  filt.frequency.setValueAtTime(freq * 6, t0);
  filt.frequency.exponentialRampToValueAtTime(freq * 1.5, t0 + dur);
  osc.connect(filt);
  sub.connect(subG).connect(filt);
  filt.connect(g).connect(dest);
  osc.start(t0);
  sub.start(t0);
  osc.stop(t0 + dur + 0.05);
  sub.stop(t0 + dur + 0.05);
}

/** Soft shaker tick for the music's off-beats. */
export function shaker(ctx: AudioContext, dest: AudioNode, t0: number, gain = 0.05): void {
  noiseBurst(ctx, dest, t0, { dur: 0.05, gain, f0: 6000, f1: 3500, type: 'highpass', q: 1 });
}
